import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useSite } from '../content';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialInquiryType?: string;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

type FieldName = 'name' | 'email' | 'phone' | 'company' | 'inquiryType' | 'message';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  company: '',
  inquiryType: '',
  message: '',
  website: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD_NAMES = ['name', 'email', 'phone', 'company', 'inquiryType', 'message'] as const;
function isFieldName(name: string): name is FieldName {
  return (FIELD_NAMES as readonly string[]).includes(name);
}

function validateField(name: FieldName, value: string, inquiryOptions: string[]): string {
  const v = value.trim();
  switch (name) {
    case 'name':
      if (!v) return 'Please enter your name.';
      if (v.length < 2) return 'Name must be at least 2 characters.';
      if (v.length > 120) return 'Name is too long.';
      return '';
    case 'email':
      if (!v) return 'Please enter your email.';
      if (!EMAIL_RE.test(v)) return 'Please enter a valid email address.';
      if (v.length > 200) return 'Email is too long.';
      return '';
    case 'phone': {
      if (!v) return 'Please enter a phone number.';
      const digits = v.replace(/\D/g, '');
      if (digits.length < 8) return 'Phone number must be at least 8 digits.';
      if (digits.length > 15) return 'Phone number is too long.';
      return '';
    }
    case 'company':
      if (!v) return 'Please enter your company.';
      if (v.length > 200) return 'Company name is too long.';
      return '';
    case 'inquiryType':
      if (!v) return 'Please choose an inquiry type.';
      if (!inquiryOptions.includes(v)) return 'Please choose a valid inquiry type.';
      return '';
    case 'message':
      if (!v) return 'Please include a message.';
      if (v.length < 10) return 'Message must be at least 10 characters.';
      if (v.length > 5000) return 'Message is too long (max 5000 characters).';
      return '';
  }
}

export function ContactModal({ isOpen, onClose, initialInquiryType }: ContactModalProps) {
  const prefersReducedMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const statusId = useId();
  const site = useSite();
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});

  useFocusTrap(dialogRef, isOpen, onClose);

  // When the modal is opened with a preselected inquiry type (e.g. from a
  // "Contact now" link under a specific capability), seed the select field.
  useEffect(() => {
    if (isOpen && initialInquiryType) {
      setFormData((prev) => ({ ...prev, inquiryType: initialInquiryType }));
    }
  }, [isOpen, initialInquiryType]);

  // Build inquiry options from site.services so they stay in sync with the
  // sections visible on the page.
  const inquiryOptions = site.services.map((s) => s.title);

  function validateAll(data: typeof emptyForm): Partial<Record<FieldName, string>> {
    const fields: FieldName[] = ['name', 'email', 'phone', 'company', 'inquiryType', 'message'];
    const next: Partial<Record<FieldName, string>> = {};
    for (const f of fields) {
      const err = validateField(f, data[f], inquiryOptions);
      if (err) next[f] = err;
    }
    return next;
  }

  function handleChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear the field's error as the user edits — don't shout at them mid-type.
    if (errors[name as FieldName]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function handleBlur(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const name = e.target.name;
    if (name === 'website' || !isFieldName(name)) return;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, e.target.value, inquiryOptions);
    setErrors((prev) => ({ ...prev, [name]: err || undefined }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (formData.website) return;

    const fieldErrors = validateAll(formData);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setTouched({
        name: true, email: true, phone: true, company: true, inquiryType: true, message: true,
      });
      // Focus the first invalid field.
      const firstInvalid = (['name', 'email', 'phone', 'company', 'inquiryType', 'message'] as FieldName[])
        .find((f) => fieldErrors[f]);
      if (firstInvalid) {
        const el = dialogRef.current?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`);
        el?.focus();
      }
      return;
    }

    setStatus('submitting');
    setErrorMessage('');
    try {
      // The static site has no /api/* proxy, so we hit the API service directly.
      // CORS on ald-solutions-api allows adlsolutions.au + adlsolutions.onrender.com.
      const apiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
        ?? 'https://ald-solutions-api.onrender.com';
      const response = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
          inquiryType: formData.inquiryType,
          message: formData.message.trim(),
        }),
      });
      const body = await response.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (!response.ok || !body?.ok) {
        throw new Error(body?.error ?? `Request failed (${response.status})`);
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
    }
  }

  function handleClose() {
    onClose();
    setTimeout(() => {
      setStatus('idle');
      setErrorMessage('');
      setFormData(emptyForm);
      setErrors({});
      setTouched({});
    }, 300);
  }

  function fieldProps(name: FieldName, errorId: string) {
    const showError = touched[name] && errors[name];
    return {
      onBlur: handleBlur,
      'aria-invalid': showError ? true : undefined,
      'aria-describedby': showError ? errorId : undefined,
      'aria-required': true,
    } as const;
  }

  function fieldError(name: FieldName, errorId: string) {
    if (!touched[name] || !errors[name]) return null;
    return (
      <p id={errorId} className="mt-1 font-sans text-xs text-[#ff8a8a]" role="alert">
        {errors[name]}
      </p>
    );
  }

  const inputClasses =
    'w-full bg-white/5 border border-textlight/20 rounded-md px-4 py-3 font-sans text-sm text-textlight placeholder:text-textlight/40 focus:outline-none focus:border-bronze focus:ring-1 focus:ring-bronze/40 transition-colors duration-200';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
        >
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative w-full max-w-lg bg-slate-dark border border-textlight/10 rounded-lg shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: 'easeOut' }}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <h3 id={titleId} className="font-serif text-2xl text-white">
                {status === 'success' ? 'Thank you' : 'Start the conversation'}
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="text-textlight/60 hover:text-textlight transition-colors p-2 -mr-2"
                aria-label="Close dialog"
              >
                <XIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            <div aria-hidden="true" className="w-10 h-[2px] bg-bronze ml-6 mb-6" />

            {status === 'success' ? (
              <div className="px-6 pb-8" role="status" aria-live="polite">
                <p className="font-sans text-textlight/80 leading-relaxed">
                  We've received your details and will be in touch shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4" noValidate>
                <div aria-hidden="true" className="sr-only">
                  <label htmlFor="contact-website">Website (leave blank)</label>
                  <input
                    id="contact-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block font-sans text-xs uppercase tracking-widest text-textlight/60 mb-1.5"
                    >
                      Name <span className="text-bronze" aria-hidden="true">*</span>
                      <span className="sr-only">required</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={inputClasses}
                      {...fieldProps('name', 'contact-name-err')}
                    />
                    {fieldError('name', 'contact-name-err')}
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block font-sans text-xs uppercase tracking-widest text-textlight/60 mb-1.5"
                    >
                      Email <span className="text-bronze" aria-hidden="true">*</span>
                      <span className="sr-only">required</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className={inputClasses}
                      {...fieldProps('email', 'contact-email-err')}
                    />
                    {fieldError('email', 'contact-email-err')}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-phone"
                      className="block font-sans text-xs uppercase tracking-widest text-textlight/60 mb-1.5"
                    >
                      Phone <span className="text-bronze" aria-hidden="true">*</span>
                      <span className="sr-only">required</span>
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0412 345 678"
                      className={inputClasses}
                      {...fieldProps('phone', 'contact-phone-err')}
                    />
                    {fieldError('phone', 'contact-phone-err')}
                  </div>
                  <div>
                    <label
                      htmlFor="contact-company"
                      className="block font-sans text-xs uppercase tracking-widest text-textlight/60 mb-1.5"
                    >
                      Company <span className="text-bronze" aria-hidden="true">*</span>
                      <span className="sr-only">required</span>
                    </label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company"
                      className={inputClasses}
                      {...fieldProps('company', 'contact-company-err')}
                    />
                    {fieldError('company', 'contact-company-err')}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-inquiry"
                    className="block font-sans text-xs uppercase tracking-widest text-textlight/60 mb-1.5"
                  >
                    Inquiry type <span className="text-bronze" aria-hidden="true">*</span>
                    <span className="sr-only">required</span>
                  </label>
                  <select
                    id="contact-inquiry"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className={`${inputClasses} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22 viewBox=%220 0 12 8%22><path fill=%22%23A0714B%22 d=%22M6 8L0 0h12z%22/></svg>')] bg-no-repeat bg-[right_1rem_center] pr-10`}
                    {...fieldProps('inquiryType', 'contact-inquiry-err')}
                  >
                    <option value="">Select an area of interest</option>
                    {inquiryOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {fieldError('inquiryType', 'contact-inquiry-err')}
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="block font-sans text-xs uppercase tracking-widest text-textlight/60 mb-1.5"
                  >
                    Message <span className="text-bronze" aria-hidden="true">*</span>
                    <span className="sr-only">required</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project or what you're looking to achieve..."
                    className={`${inputClasses} resize-none`}
                    {...fieldProps('message', 'contact-message-err')}
                  />
                  {fieldError('message', 'contact-message-err')}
                </div>

                <div id={statusId} role="status" aria-live="polite" className="min-h-[1.25rem]">
                  {status === 'error' && (
                    <p className="font-sans text-sm text-[#ff8a8a]">{errorMessage}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  aria-describedby={statusId}
                  className="w-full mt-2 px-8 py-3.5 bg-burgundy hover:bg-burgundy/80 disabled:opacity-60 disabled:cursor-not-allowed text-textlight font-sans tracking-widest uppercase text-xs rounded-full transition-all duration-300 shadow-lg min-h-[44px]"
                >
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </button>

                <p className="text-center font-sans text-[11px] text-textlight/50 pt-1">
                  We'll respond within 24 hours.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
